import { useCallback, useEffect, useState, useRef } from 'react'
import { getKyousouApiClient } from '../services/network'
import { EnterpriseSummary } from '../clients/kyousou_api_client'

export interface AsiatoEntry {
  displayCount: number
  stayTimeText: string
  latestDisplayAt: string
  enterprise: EnterpriseSummary
}

// API統計データの型定義
export interface StatData {
  /** 総訪問回数 */
  displayCount: number
  /** 訪問企業数 */
  displayEnterpriseCount: number
  /** 平均マッチング率 */
  matchingRate: number
  /** チェック済み企業リスト */
  checkedEnterprises: AsiatoEntry[]
}

export const useOwnAsiato = (
  start?: string,
  end?: string,
  category: 'all' | 'new' | 'repeat' = 'all',
  limit = 10
): {
  totalCount: number
  cursor: number
  isLoading: boolean
  isLoadingMore: boolean
  hasMore: boolean
  asiatoList: AsiatoEntry[]
  statData: StatData | null
  loadMore: () => void
  changeCategory: (newCategory: 'all' | 'new' | 'repeat') => void
  changePeriod: (newStart?: string, newEnd?: string) => void
} => {
  const apiClient = getKyousouApiClient()
  const [totalCount, setTotalCount] = useState(0)
  const [cursor, setCursor] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [asiatoList, setAsiatoList] = useState<AsiatoEntry[]>([])
  const [statData, setStatData] = useState<StatData | null>(null)
  const [currentCategory, setCurrentCategory] = useState(category)
  const [currentStart, setCurrentStart] = useState(start)
  const [currentEnd, setCurrentEnd] = useState(end)
  const abortControllerRef = useRef<AbortController | null>(null)
  const hasMore = cursor < totalCount

  const load = useCallback(
    async (isLoadMore = false) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      abortControllerRef.current = new AbortController()

      if (isLoadMore) {
        setIsLoadingMore(true)
      } else {
        setIsLoading(true)
        setCursor(0)
      }

      try {
        const response = await apiClient.stat.statList({
          start: currentStart,
          end: currentEnd,
          category: currentCategory,
          cursor: isLoadMore ? cursor : 0,
          limit: limit,
        })

        const responseData = response.data
        const newAsiatoList = responseData.checkedEnterprises || []
        setTotalCount(responseData.total || newAsiatoList.length)

        if (!abortControllerRef.current?.signal.aborted) {
          setAsiatoList((prevList) => {
            if (!isLoadMore) return newAsiatoList
            return [...prevList, ...newAsiatoList]
          })

          setCursor((prev) =>
            isLoadMore ? prev + newAsiatoList.length : newAsiatoList.length
          )

          if (!isLoadMore) {
            setStatData({
              displayCount: responseData.displayCount,
              displayEnterpriseCount: responseData.displayEnterpriseCount,
              matchingRate: responseData.matchingRate,
              checkedEnterprises: responseData.checkedEnterprises,
            })
          }
        }
      } catch (error) {
        if (!abortControllerRef.current?.signal.aborted) {
          console.error('足跡データの取得に失敗:', error)
          if (!isLoadMore) {
            setAsiatoList([])
            setTotalCount(0)
            setCursor(0)
            setStatData(null)
          }
        }
      } finally {
        if (!abortControllerRef.current?.signal.aborted) {
          if (isLoadMore) {
            setIsLoadingMore(false)
          } else {
            setIsLoading(false)
          }
        }
      }
    },
    [limit, currentCategory, currentStart, currentEnd, cursor]
  )

  useEffect(() => {
    load(false)

    return () => {
      abortControllerRef.current?.abort()
    }
  }, [currentCategory, currentStart, currentEnd])

  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore || isLoading) return
    load(true)
  }, [isLoadingMore, hasMore, isLoading, load])

  const changeCategory = useCallback(
    (newCategory: 'all' | 'new' | 'repeat') => {
      if (newCategory !== currentCategory) {
        setCurrentCategory(newCategory)
        setAsiatoList([])
        setTotalCount(0)
        setCursor(0)
      }
    },
    [currentCategory]
  )

  const changePeriod = useCallback((newStart?: string, newEnd?: string) => {
    setCurrentStart(newStart)
    setCurrentEnd(newEnd)
    setAsiatoList([])
    setTotalCount(0)
    setCursor(0)
  }, [])

  return {
    totalCount,
    cursor,
    isLoading,
    isLoadingMore,
    hasMore,
    asiatoList,
    statData,
    loadMore,
    changeCategory,
    changePeriod,
  }
}
